import type {BaseComponentPart} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import type {StreamComponentType} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import {PillType} from '@Components/pill/pill.types';
import type {ShownMoment} from '@Utils/friendly-moment.util';

/**
 * Known clickable entity types. A value here must have a matching registration in
 * entity-card/actions/ (see entity-card-action-registry). A card whose entityType is
 * absent or unregistered renders as a static, non-clickable card.
 */
export enum EntityCardEntityType {
  EVENT = 'event',
  PDF = 'pdf',
  SOCIAL_POST = 'social_post',
}

export enum EntityCardStatusTone {
  NEUTRAL = 'neutral',
  BRAND = 'brand',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
}

/** One fact on the card's meta line: its text, or a moment the card words when it shows it. */
export type EntityCardMetaEntry = string | ShownMoment;

export interface EntityCardStatus {
  label: string;
  tone: EntityCardStatusTone;
}

/**
 * Generic, entity-agnostic card for a real (non-draft) entity — a thumbnail with a title, a text meta
 * line and an optional status pill. Reusable across entities (events, PDFs, social posts, …).
 *
 * When entityType is set to a registered type AND an `entity` payload is present, the card becomes
 * clickable and its onClick is resolved from the entity-card action registry. A `refreshable` card
 * whose action can fetch a fresh copy keeps polling until the server says it has settled.
 */
export interface EntityCardProps {
  title?: string;
  meta: EntityCardMetaEntry[];
  /** A fact the meta line ends with that needs the user's attention, such as where something failed. */
  alert?: string;
  mediaUrl?: string;
  /** Icon-font class (e.g. 'icon-doc') shown in a brand-tinted box when there is no mediaUrl — used for file/document entities like PDFs. */
  icon?: string;
  mediaCount?: number;
  status?: EntityCardStatus;
  entityType?: EntityCardEntityType;
  entity?: Record<string, unknown>;
  refreshable?: boolean;
  /** Unix time (seconds) from which the card starts refreshing, e.g. the moment the entity is due to change state. */
  refreshAt?: number;
}

export type EntityCardComponentPart = BaseComponentPart<StreamComponentType.ENTITY_CARD, EntityCardProps>;

export const THUMBS_UP_ICON = 'icon-thumbs-up';

export const THUMBS_DOWN_ICON = 'icon-thumbs-down';

export const ENTITY_CARD_META_SEPARATOR = '  ·  ';

export const ENTITY_CARD_REFRESH_INTERVAL_MS = 3000;

export const ENTITY_CARD_REFRESH_TIMEOUT_MS = 900000;

export const MILLISECONDS_PER_SECOND = 1000;

/** Longest delay a browser timer honours; a later moment is left for the card's next render to schedule. */
export const MAX_TIMER_DELAY_MS = 2147483647;

export const STATUS_TONE_PILL_TYPE: Record<EntityCardStatusTone, PillType> = {
  [EntityCardStatusTone.NEUTRAL]: PillType.NEUTRAL,
  [EntityCardStatusTone.BRAND]: PillType.BRAND,
  [EntityCardStatusTone.SUCCESS]: PillType.SUCCESS,
  [EntityCardStatusTone.WARNING]: PillType.WARNING,
  [EntityCardStatusTone.ERROR]: PillType.ERROR,
};
